import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Trophy, Target, XCircle, MinusCircle, Clock, ArrowLeft, CheckCircle, X, ChevronLeft, ChevronRight, BookOpen } from 'lucide-react';

export default function TestResults() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQ, setCurrentQ] = useState(0);

  useEffect(() => {
    api.get(`/student/attempts/${attemptId}/result`).then(r => setResult(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [attemptId]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-8 h-8 border-4 border-[#7551e8] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!result) return <div className="text-center py-12"><p>Result not found</p></div>;

  const pieData = [
    { name: 'Correct', value: result.total_correct || 0, color: '#10B981' },
    { name: 'Incorrect', value: result.total_incorrect || 0, color: '#EF4444' },
    { name: 'Unattempted', value: result.total_unattempted || 0, color: '#E4E4E7' },
  ];

  const timeData = (result.question_results || []).map((qr, i) => ({
    name: `Q${i + 1}`,
    time: Math.round(result.time_spent?.[qr.question_id] || 0),
  }));

  const avgTime = timeData.length > 0 ? Math.round(timeData.reduce((a, b) => a + b.time, 0) / timeData.length) : 0;

  const questions = result.questions || [];
  const qResults = result.question_results || [];
  const getStatus = (qId) => {
    const qr = qResults.find(r => r.question_id === qId);
    return qr?.status || 'unattempted';
  };
  const getUserAnswer = (qId) => {
    const qr = qResults.find(r => r.question_id === qId);
    return qr?.user_answer || null;
  };

  const q = questions[currentQ];
  const qr = q ? qResults.find(r => r.question_id === q.id) : null;
  const status = qr?.status || 'unattempted';

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="test-results">
      <Button variant="ghost" className="mb-4" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
        <ArrowLeft size={16} className="mr-2" /> Back to Dashboard
      </Button>

      <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-semibold text-[#0A0A0A] mb-2" data-testid="result-title">
        {result.test_title || 'Test'} - Results
      </h1>

      {/* Score Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        <Card className="p-3 sm:p-4 border border-zinc-200" data-testid="result-score">
          <div className="flex items-center gap-1.5 mb-1"><Trophy size={14} className="text-[#7551e8]" /><span className="text-xs text-[#52525B]">Score</span></div>
          <p className="font-['Outfit'] text-xl sm:text-2xl font-semibold">{result.score?.toFixed(1)}<span className="text-xs sm:text-sm text-[#52525B]">/{result.total_marks || 100}</span></p>
        </Card>
        <Card className="p-3 sm:p-4 border border-zinc-200" data-testid="result-correct">
          <div className="flex items-center gap-1.5 mb-1"><Target size={14} className="text-green-600" /><span className="text-xs text-[#52525B]">Correct</span></div>
          <p className="font-['Outfit'] text-xl sm:text-2xl font-semibold text-green-600">{result.total_correct}</p>
        </Card>
        <Card className="p-3 sm:p-4 border border-zinc-200" data-testid="result-incorrect">
          <div className="flex items-center gap-1.5 mb-1"><XCircle size={14} className="text-red-500" /><span className="text-xs text-[#52525B]">Wrong</span></div>
          <p className="font-['Outfit'] text-xl sm:text-2xl font-semibold text-red-500">{result.total_incorrect}</p>
        </Card>
        <Card className="p-3 sm:p-4 border border-zinc-200" data-testid="result-unattempted">
          <div className="flex items-center gap-1.5 mb-1"><MinusCircle size={14} className="text-zinc-400" /><span className="text-xs text-[#52525B]">Skipped</span></div>
          <p className="font-['Outfit'] text-xl sm:text-2xl font-semibold text-zinc-500">{result.total_unattempted}</p>
        </Card>
        <Card className="p-3 sm:p-4 border border-zinc-200 col-span-2 sm:col-span-1" data-testid="result-percentile">
          <div className="flex items-center gap-1.5 mb-1"><Clock size={14} className="text-blue-500" /><span className="text-xs text-[#52525B]">Percentile</span></div>
          <p className="font-['Outfit'] text-xl sm:text-2xl font-semibold text-blue-600">{result.percentile}%</p>
        </Card>
      </div>

      <Tabs defaultValue="review" className="space-y-4">
        <TabsList>
          <TabsTrigger value="review" data-testid="tab-review"><BookOpen size={14} className="mr-1.5" />Solutions</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
        </TabsList>

        {/* CBT-Style Solution Review */}
        <TabsContent value="review" data-testid="question-review-section">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Question Panel */}
            <div className="flex-1 min-w-0">
              {q && (
                <Card className="border border-zinc-200 overflow-hidden" data-testid={`review-q-${currentQ + 1}`}>
                  {/* Question Header */}
                  <div className={`px-4 sm:px-5 py-3 border-b flex items-center justify-between ${
                    status === 'correct' ? 'bg-green-50 border-green-200' : status === 'incorrect' ? 'bg-red-50 border-red-200' : 'bg-zinc-50 border-zinc-200'
                  }`}>
                    <div className="flex items-center gap-2 sm:gap-3">
                      <span className="font-['Outfit'] font-semibold text-sm sm:text-base text-[#0A0A0A]">Question {currentQ + 1}</span>
                      <Badge className={`text-xs ${status === 'correct' ? 'bg-green-100 text-green-700' : status === 'incorrect' ? 'bg-red-100 text-red-700' : 'bg-zinc-100 text-zinc-500'} border-0`}>
                        {status === 'correct' ? <CheckCircle size={10} className="mr-1" /> : status === 'incorrect' ? <X size={10} className="mr-1" /> : null}
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </Badge>
                    </div>
                    {result.time_spent?.[q.id] && (
                      <span className="text-xs text-[#52525B] flex items-center gap-1"><Clock size={12} />{Math.round(result.time_spent[q.id])}s</span>
                    )}
                  </div>

                  {/* Question Body */}
                  <div className="p-4 sm:p-6">
                    <p className="text-[#0A0A0A] text-sm sm:text-base mb-5 whitespace-pre-wrap leading-relaxed">{q.question_text}</p>

                    {/* Options */}
                    <div className="space-y-3">
                      {['a', 'b', 'c', 'd'].map(opt => {
                        const isCorrect = q.correct_option === opt;
                        const isUser = getUserAnswer(q.id) === opt;
                        let cls = 'border border-zinc-200 bg-white hover:bg-zinc-50';
                        let indicator = null;
                        if (isCorrect) {
                          cls = 'border-2 border-green-500 bg-green-50';
                          indicator = <CheckCircle size={16} className="text-green-600 shrink-0" />;
                        } else if (isUser && !isCorrect) {
                          cls = 'border-2 border-red-400 bg-red-50';
                          indicator = <X size={16} className="text-red-500 shrink-0" />;
                        }
                        return (
                          <div key={opt} className={`flex items-center gap-3 p-3 sm:p-4 rounded-lg transition-colors ${cls}`} data-testid={`option-${opt}`}>
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 ${
                              isCorrect ? 'bg-green-500 text-white' : isUser ? 'bg-red-400 text-white' : 'bg-zinc-100 text-zinc-600'
                            }`}>{opt.toUpperCase()}</span>
                            <span className="flex-1 text-sm sm:text-base">{q[`option_${opt}`]}</span>
                            {indicator}
                          </div>
                        );
                      })}
                    </div>

                    {/* Your Answer vs Correct */}
                    {status === 'incorrect' && (
                      <div className="mt-4 flex flex-wrap gap-3 text-sm">
                        <span className="text-red-600">Your answer: <strong>{getUserAnswer(q.id)?.toUpperCase()}</strong></span>
                        <span className="text-green-600">Correct answer: <strong>{q.correct_option?.toUpperCase()}</strong></span>
                      </div>
                    )}

                    {/* Explanation */}
                    {q.explanation && (
                      <div className="mt-5 bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-800">
                        <strong className="block mb-1">Explanation:</strong>
                        <span className="leading-relaxed">{q.explanation}</span>
                      </div>
                    )}
                  </div>

                  {/* Navigation */}
                  <div className="px-4 sm:px-5 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between">
                    <Button variant="outline" size="sm" disabled={currentQ === 0} onClick={() => setCurrentQ(p => p - 1)} data-testid="prev-q-btn">
                      <ChevronLeft size={16} className="mr-1" /> Previous
                    </Button>
                    <span className="text-xs text-[#52525B]">{currentQ + 1} of {questions.length}</span>
                    <Button variant="outline" size="sm" disabled={currentQ >= questions.length - 1} onClick={() => setCurrentQ(p => p + 1)} data-testid="next-q-btn">
                      Next <ChevronRight size={16} className="ml-1" />
                    </Button>
                  </div>
                </Card>
              )}
            </div>

            {/* Question Palette */}
            <div className="lg:w-64 shrink-0">
              <Card className="border border-zinc-200 p-4 sticky top-4">
                <h3 className="font-['Outfit'] font-medium text-sm mb-3">Question Palette</h3>
                <div className="grid grid-cols-5 sm:grid-cols-8 lg:grid-cols-5 gap-2 mb-4">
                  {questions.map((qq, i) => {
                    const s = getStatus(qq.id);
                    return (
                      <button
                        key={qq.id}
                        onClick={() => setCurrentQ(i)}
                        className={`w-9 h-9 rounded-lg text-xs font-semibold transition-all flex items-center justify-center ${
                          i === currentQ ? 'ring-2 ring-[#7551e8] ring-offset-1' : ''
                        } ${
                          s === 'correct' ? 'bg-green-500 text-white' :
                          s === 'incorrect' ? 'bg-red-400 text-white' :
                          'bg-zinc-200 text-zinc-600'
                        }`}
                        data-testid={`palette-q-${i + 1}`}
                      >
                        {i + 1}
                      </button>
                    );
                  })}
                </div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-green-500" /> Correct ({result.total_correct})</div>
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-red-400" /> Incorrect ({result.total_incorrect})</div>
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-zinc-200" /> Unattempted ({result.total_unattempted})</div>
                </div>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="analytics">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="p-4 sm:p-6 border border-zinc-200" data-testid="pie-chart-card">
              <h3 className="font-['Outfit'] font-medium mb-4">Score Breakdown</h3>
              <div className="h-52 sm:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={75} paddingAngle={2} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                      {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <Card className="p-4 sm:p-6 border border-zinc-200" data-testid="time-chart-card">
              <h3 className="font-['Outfit'] font-medium mb-1">Time per Question</h3>
              <p className="text-xs text-[#52525B] mb-4">Average: {avgTime}s per question</p>
              <div className="h-52 sm:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={timeData.slice(0, 30)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="time" fill="#7551e8" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
